import isUndefined from 'lodash.isundefined';
import omitBy from 'lodash.omitby';

import { decode, getCursor, match } from '@spazious/dollhouse-selector';

import { shallowNodeDefaultValues, shallowNodeRequiredKeys } from '../constants/index.js';
import { FShallowGraph } from '../types/index.js';
import { getShallowNodeValue, setShallowNodeValue } from '../utils/index.js';

import {
  GraphAction,
  GraphActionType,
  GraphAddAction,
  GraphInitAction,
  GraphLoadAction,
  GraphRemoveAction,
  GraphSetNameAction,
  GraphSetSpaceAction,
  GraphUpdateAction,
} from './types.js';

/**
 * Adds a node to the graph
 */
export const graphAddActionReducer = <TState extends FShallowGraph>(
  state: TState,
  action: GraphAddAction
): TState => {
  const { selector, node } = action.payload;
  return {
    ...state,
    cursor: getCursor(selector, state.cursor),
    nodes: {
      ...state.nodes,
      [selector]: omitBy(node, isUndefined),
    },
  } as TState;
};

export const graphUpdateReducer = <TState extends FShallowGraph>(
  state: TState,
  action: GraphUpdateAction
): TState => {
  const { selector } = action.payload;
  const node = state.nodes[selector];

  if (!node) {
    throw new Error(`Node with selector ${selector} not found`);
  }

  const defaultValue = getShallowNodeValue(shallowNodeDefaultValues, action.payload.path as any);
  const value = defaultValue === action.payload.value ? undefined : action.payload.value;
  const newNode = setShallowNodeValue(node, action.payload.path, value);
  const nodeKeys = Object.keys(newNode);
  const partialKeys = nodeKeys.filter((key) => !shallowNodeRequiredKeys.includes(key as any));

  if (partialKeys.length === 0) {
    return graphRemoveReducer(state, { type: GraphActionType.Remove, payload: { selector } });
  }

  return {
    ...state,
    nodes: {
      ...state.nodes,
      [selector]: newNode,
    },
  } as TState;
};

export const graphRemoveReducer = <TState extends FShallowGraph>(
  state: TState,
  action: GraphRemoveAction
): TState => {
  const { selector } = action.payload;

  const decoded = decode(selector);
  // Remove all the children if the parent is removed
  const cleanupNodes = Object.entries(state.nodes).reduce((acc, [nodeSelector, node]) => {
    return !match(decoded, nodeSelector) ? { ...acc, [nodeSelector]: node } : acc;
  }, {});

  return {
    ...state,
    nodes: cleanupNodes,
  };
};

export const graphInitReducer = <TState extends FShallowGraph>(
  _: TState,
  action: GraphInitAction
): TState => {
  return action.payload as TState;
};

export const graphLoadReducer = <TState extends FShallowGraph>(
  _: TState,
  action: GraphLoadAction
): TState => {
  return action.payload as TState;
};

export const graphSetNameReducer = <TState extends FShallowGraph>(
  state: TState,
  action: GraphSetNameAction
): TState => {
  return {
    ...state,
    name: action.payload.name,
  } as TState;
};

export const graphSetSpaceReducer = <TState extends FShallowGraph>(
  state: TState,
  action: GraphSetSpaceAction
): TState => {
  return {
    ...state,
    space: action.payload.id,
  } as TState;
};

export const graphReducerMapping = {
  [GraphActionType.Init]: graphInitReducer,
  [GraphActionType.Load]: graphLoadReducer,
  [GraphActionType.Add]: graphAddActionReducer,
  [GraphActionType.Update]: graphUpdateReducer,
  [GraphActionType.Remove]: graphRemoveReducer,
  [GraphActionType.SetName]: graphSetNameReducer,
  [GraphActionType.SetSpace]: graphSetSpaceReducer,
};

export const graphReducer = <TState extends FShallowGraph, TAction extends GraphAction>(
  state: TState,
  action: TAction
): TState => {
  return graphReducerMapping[action.type](state, action as any) as TState;
};
